import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { BellOff, Inbox, Loader2, Mail, MessageCircle, Search, Smartphone } from "lucide-react";
import { toast } from "sonner";

import { NotificacaoCanal, NotificacaoCanalMap, NotificacaoTemplate } from "@workflow/types";
import {
  DefinirRegraDTO,
  MatrizRegrasDTO,
  RegraCanalDTO,
  RegraTemplateCanalDTO,
  RegraTemplateDTO,
} from "@/types/notificacao-regra.type";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { notificacaoRegraService } from "@/services/api/NotificacaoRegraService";
import {
  notificacoesAdminKeys,
  notificacoesAdminQueries,
} from "@/services/api/queries/notificacoes-admin.queries";
import { cn } from "@/lib/utils";

const CANAL_ICON: Record<NotificacaoCanal, typeof Mail> = {
  [NotificacaoCanal.EMAIL]: Mail,
  [NotificacaoCanal.WHATSAPP]: MessageCircle,
  [NotificacaoCanal.SMS]: Smartphone,
  [NotificacaoCanal.INBOX]: Inbox,
};

/** Largura das colunas de canal — casa o cabeçalho do grupo com as linhas. */
const COL_CANAL = "w-24 shrink-0 text-center";

/** O que o admin está prestes a pausar — vira o texto do diálogo de motivo. */
interface AlvoPausa {
  canal: NotificacaoCanal;
  template: NotificacaoTemplate | null;
  titulo: string;
}

export function RegrasEnvioPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery(notificacoesAdminQueries.regras());

  const [busca, setBusca] = useState("");
  const [alvo, setAlvo] = useState<AlvoPausa | null>(null);
  const [motivo, setMotivo] = useState("");

  const definir = useMutation({
    mutationFn: (dto: DefinirRegraDTO) => notificacaoRegraService.definir(dto),
    onSuccess: (_, dto) => {
      qc.invalidateQueries({ queryKey: notificacoesAdminKeys.regras });
      toast.success(dto.habilitado ? "Envio religado" : "Envio pausado");
      fecharDialogo();
    },
    onError: () => toast.error("Não foi possível salvar a regra"),
  });

  function fecharDialogo() {
    setAlvo(null);
    setMotivo("");
  }

  /** Religar é direto; pausar passa pelo diálogo, porque o motivo é obrigatório. */
  function alternar(
    canal: NotificacaoCanal,
    template: NotificacaoTemplate | null,
    habilitado: boolean,
    titulo: string,
  ) {
    if (habilitado) return setAlvo({ canal, template, titulo });
    definir.mutate({ canal, template, habilitado: true });
  }

  const grupos = useMemo(() => agruparTemplates(data, busca), [data, busca]);

  if (isLoading) return <Skeleton className="h-96 w-full rounded-xl" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Chave geral: resolve o incêndio com um toggle, por isso vem primeiro. */}
      <div className="grid gap-4 sm:grid-cols-2">
        {data.canais.map((regra) => (
          <CartaoCanal
            key={regra.canal}
            regra={regra}
            pendente={definir.isPending}
            onToggle={() =>
              alternar(
                regra.canal,
                null,
                regra.habilitado,
                `todos os envios por ${NotificacaoCanalMap[regra.canal]}`,
              )
            }
          />
        ))}
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar notificação..."
          className="pl-9"
        />
      </div>

      {grupos.length === 0 ? (
        <p className="rounded-xl border border-dashed py-12 text-center text-sm text-muted-foreground">
          Nenhuma notificação encontrada.
        </p>
      ) : (
        <Accordion
          // Remonta ao entrar/sair da busca para que o resultado já venha aberto.
          key={busca ? "busca" : "todos"}
          type="multiple"
          defaultValue={busca ? grupos.map(([grupo]) => grupo) : []}
          className="space-y-3"
        >
          {grupos.map(([grupo, templates]) => {
            const pausados = templates.reduce(
              (n, t) =>
                n +
                t.canais.filter((c) => c.implementado && !c.habilitado && !c.motivoHerdado).length,
              0,
            );
            return (
              <AccordionItem
                key={grupo}
                value={grupo}
                className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <span className="flex flex-1 items-center gap-2.5 text-left">
                    <span className="text-sm font-semibold">{grupo}</span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {templates.length}
                    </span>
                    {pausados > 0 && (
                      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                        {pausados} pausado{pausados > 1 ? "s" : ""}
                      </span>
                    )}
                  </span>
                </AccordionTrigger>

                <AccordionContent className="pb-0">
                  {/* Rótulo do canal uma vez por grupo, em vez de repetir por linha. */}
                  <div className="flex items-center gap-4 border-y border-border/60 bg-muted/40 px-4 py-1.5">
                    <span className="flex-1" />
                    {data.canais.map((c) => (
                      <span
                        key={c.canal}
                        className={cn(
                          COL_CANAL,
                          "text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                        )}
                      >
                        {NotificacaoCanalMap[c.canal]}
                      </span>
                    ))}
                  </div>

                  <div className="divide-y divide-border/50">
                    {templates.map((t) => (
                      <div
                        key={t.template}
                        className="flex items-center gap-4 px-4 py-2.5 transition-colors hover:bg-muted/40"
                      >
                        <p className="flex-1 truncate text-sm">{t.rotulo}</p>
                        {data.canais.map((c) => {
                          const celula = t.canais.find((x) => x.canal === c.canal);
                          if (!celula) return <span key={c.canal} className={COL_CANAL} />;
                          return (
                            <CelulaCanal
                              key={c.canal}
                              celula={celula}
                              pendente={definir.isPending}
                              onToggle={() =>
                                alternar(
                                  celula.canal,
                                  t.template,
                                  celula.habilitado,
                                  `"${t.rotulo}" por ${NotificacaoCanalMap[celula.canal]}`,
                                )
                              }
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <Dialog open={!!alvo} onOpenChange={(open) => !open && fecharDialogo()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pausar {alvo?.titulo}?</DialogTitle>
            <DialogDescription>
              Ninguém receberá esses envios até você religar. O motivo aparece para quem for
              investigar depois.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Por que está pausando? Ex.: provedor instável"
            maxLength={500}
            rows={3}
          />

          <DialogFooter>
            <Button variant="outline" onClick={fecharDialogo}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={!motivo.trim() || definir.isPending}
              onClick={() =>
                alvo &&
                definir.mutate({
                  canal: alvo.canal,
                  template: alvo.template,
                  habilitado: false,
                  motivo: motivo.trim(),
                })
              }
            >
              {definir.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Pausar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Chave geral do canal. Tingido pelo estado (verde ativo / âmbar pausado) e com
 * o motivo da pausa dentro do próprio card — quem viu o card já sabe o porquê.
 */
function CartaoCanal({
  regra,
  pendente,
  onToggle,
}: {
  regra: RegraCanalDTO;
  pendente: boolean;
  onToggle: () => void;
}) {
  const Icon = CANAL_ICON[regra.canal];
  const nome = NotificacaoCanalMap[regra.canal];
  const pausado = !regra.habilitado;

  const rastro =
    [
      regra.alteradoEm && `desde ${format(new Date(regra.alteradoEm), "dd/MM/yyyy")}`,
      regra.alteradoPor && `por ${regra.alteradoPor}`,
    ]
      .filter(Boolean)
      .join(" · ") || "sem registro";

  return (
    <div
      className={cn(
        "rounded-xl border p-4 shadow-sm transition-colors",
        pausado
          ? "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30"
          : "border-border bg-card",
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className={cn(
              "grid size-10 shrink-0 place-items-center rounded-lg",
              pausado
                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                : "bg-muted text-muted-foreground",
            )}
          >
            {pausado ? <BellOff className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
          </span>
          <div className="min-w-0 space-y-0.5">
            <p className="flex items-center gap-2 text-sm font-semibold">
              {nome}
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  pausado
                    ? "bg-amber-500/20 text-amber-700 dark:text-amber-300"
                    : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
                )}
              >
                {pausado ? "Pausado" : "Ativo"}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              {pausado
                ? "Nenhuma notificação sai por este canal."
                : "As regras por notificação abaixo continuam valendo."}
            </p>
          </div>
        </div>

        <label className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {pausado ? "Pausado" : "Ativo"}
          </span>
          <Switch checked={!pausado} disabled={pendente} onCheckedChange={onToggle} />
        </label>
      </div>

      {pausado && (
        <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
          {regra.motivo || "Sem motivo registrado"}
          <span className="ml-1 opacity-70">— {rastro}</span>
        </p>
      )}
    </div>
  );
}

function CelulaCanal({
  celula,
  pendente,
  onToggle,
}: {
  celula: RegraTemplateCanalDTO;
  pendente: boolean;
  onToggle: () => void;
}) {
  const nome = NotificacaoCanalMap[celula.canal];
  const canalPausado = !!celula.motivoHerdado;

  // Sem handler o toggle mentiria: o envio nunca sairia por aqui, ligado ou não.
  if (!celula.implementado) {
    return (
      <span
        className={cn(COL_CANAL, "text-xs text-muted-foreground/50")}
        title={`${nome}: não implementado`}
      >
        —
      </span>
    );
  }

  return (
    <span className={cn(COL_CANAL, canalPausado && "opacity-40")}>
      <Switch
        checked={celula.habilitado}
        disabled={pendente || canalPausado}
        onCheckedChange={onToggle}
        aria-label={`${nome}: ${celula.habilitado ? "ativo" : "pausado"}`}
        title={canalPausado ? `Canal ${nome} pausado — a pausa do canal manda` : undefined}
      />
    </span>
  );
}

/** Agrupa por `grupo` preservando a ordem do enum e aplicando a busca. */
function agruparTemplates(
  data: MatrizRegrasDTO | undefined,
  busca: string,
): [string, RegraTemplateDTO[]][] {
  if (!data) return [];
  const termo = busca.trim().toLowerCase();
  const mapa = new Map<string, RegraTemplateDTO[]>();

  for (const t of data.templates) {
    if (termo && !t.rotulo.toLowerCase().includes(termo) && !t.grupo.toLowerCase().includes(termo))
      continue;
    const lista = mapa.get(t.grupo);
    if (lista) lista.push(t);
    else mapa.set(t.grupo, [t]);
  }

  return Array.from(mapa.entries());
}
